import { showNotificationGlobal } from "@/stores/useNotificationStore";

/// Bookkeeping for one in-flight dataset drop. The notification store holds a
/// single toast, so the per-table WS ACK toasts of a dataset's members would
/// overwrite each other and the user only ever sees the last table's result.
/// Instead, members are swallowed into one live "(k/N)" progress toast and a
/// final dataset summary. Success/waiting ACKs carry the table NAME (matched
/// via a count-map — names can repeat across parent models); failure ACKs
/// carry no identity at all, so a failure during an active batch is
/// attributed to it anonymously.
interface DatasetImportBatch {
  datasetName: string;
  /// name → dispatched members still awaiting a terminal result.
  awaiting: Map<string, number>;
  outstanding: number;
  total: number;
  succeeded: number;
  failed: number;
  /// Acknowledged but landing later (waiting for release / pending approval).
  deferred: number;
  /// Pre-computed skip summary ("2 already imported", …) for the final toast.
  skipParts: string[];
  timeout: number;
}

let batch: DatasetImportBatch | null = null;

/// An ACK that never arrives (e.g. the server renamed the table at
/// fulfillment, so the name can't match) must not strand the progress toast.
const BATCH_TIMEOUT_MS = 90_000;

export type DatasetMemberOutcome = "success" | "deferred" | "failure";

export const beginDatasetImportBatch = (
  datasetName: string,
  expectedNames: string[],
  skipParts: string[],
): void => {
  // A new drop supersedes a still-open batch: settle the old one first.
  if (batch) finalizeBatch();
  const awaiting = new Map<string, number>();
  for (const name of expectedNames) {
    awaiting.set(name, (awaiting.get(name) ?? 0) + 1);
  }
  batch = {
    datasetName,
    awaiting,
    outstanding: expectedNames.length,
    total: expectedNames.length,
    succeeded: 0,
    failed: 0,
    deferred: 0,
    skipParts,
    timeout: window.setTimeout(finalizeBatch, BATCH_TIMEOUT_MS),
  };
  showProgress();
};

/// Terminal result for a named member — WS success/waiting ACKs, computed
/// members resolved from their HTTP call, and dispatch-time throws. Returns
/// true when the result belonged to the active batch; the caller must then
/// skip its own per-table toast.
export const resolveDatasetMember = (
  tableName: string,
  outcome: DatasetMemberOutcome,
): boolean => {
  if (!batch) return false;
  const left = batch.awaiting.get(tableName);
  if (!left) return false;
  if (left === 1) batch.awaiting.delete(tableName);
  else batch.awaiting.set(tableName, left - 1);
  settle(outcome);
  return true;
};

/// TableImportFailure ACKs carry only a reason, no table identity.
export const resolveDatasetMemberFailureAnonymous = (): boolean => {
  if (!batch || batch.outstanding === 0) return false;
  settle("failure");
  return true;
};

const settle = (outcome: DatasetMemberOutcome): void => {
  if (!batch) return;
  batch.outstanding -= 1;
  if (outcome === "success") batch.succeeded += 1;
  else if (outcome === "deferred") batch.deferred += 1;
  else batch.failed += 1;
  if (batch.outstanding === 0) finalizeBatch();
  else showProgress();
};

const showProgress = (): void => {
  if (!batch) return;
  const done = batch.total - batch.outstanding;
  showNotificationGlobal(
    `Importing dataset "${batch.datasetName}"… (${done}/${batch.total})`,
    "default",
    undefined,
    0,
  );
};

const finalizeBatch = (): void => {
  if (!batch) return;
  const b = batch;
  batch = null;
  window.clearTimeout(b.timeout);
  const clean =
    b.failed === 0 &&
    b.deferred === 0 &&
    b.outstanding === 0 &&
    b.skipParts.length === 0;
  if (clean) {
    showNotificationGlobal(
      `Dataset "${b.datasetName}" imported successfully`,
      "success",
    );
    return;
  }
  const parts = [...b.skipParts];
  if (b.failed > 0) parts.push(`${b.failed} failed`);
  if (b.deferred > 0) parts.push(`${b.deferred} waiting for release`);
  if (b.outstanding > 0) parts.push(`${b.outstanding} unresolved`);
  const suffix = parts.length > 0 ? ` — ${parts.join(", ")}` : "";
  showNotificationGlobal(
    `Dataset "${b.datasetName}": ${b.succeeded} of ${b.total} table${
      b.total === 1 ? "" : "s"
    } imported${suffix}`,
    b.failed > 0 || b.outstanding > 0 ? "warning" : "success",
  );
};

/// Test-only: drop any open batch without toasting.
export const __resetDatasetImportBatch = (): void => {
  if (batch) window.clearTimeout(batch.timeout);
  batch = null;
};
